import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CallDirection } from '../../types';
import { CallActivityEventTimeline } from '../activity-log/CallActivityEventTimeline';
import {
  extractTraceSummaryFromEvents,
  formatTraceTimestamp,
  getTraceDisplayName,
} from '../activity-log/call-activity-log.utils';
import { useCallTraceEvents } from '../activity-log/useCallTraceEvents';
import { CallDirectionBadge } from '../call/CallDirectionBadge';
import { formatPhoneForDisplay } from '../../utils/format-phone.util';
import activityLogStyles from '../activity-log/CallActivityLogPanel.module.css';
import styles from './AcwCallTraceTimeline.module.css';

interface AcwCallTraceTimelineProps {
  callTraceId?: string | null;
  cxoneContactId?: string | null;
  fallbackDirection?: CallDirection | null;
}

export function AcwCallTraceTimeline({
  callTraceId,
  cxoneContactId,
  fallbackDirection,
}: AcwCallTraceTimelineProps) {
  const { t } = useTranslation(['acw', 'activityLog']);
  const { events, isLoading, error } = useCallTraceEvents({ callTraceId, cxoneContactId });

  const summary = useMemo(() => extractTraceSummaryFromEvents(events), [events]);
  const direction = summary.direction ?? fallbackDirection ?? null;
  const displayName = getTraceDisplayName(summary);
  const hasSummaryContent = Boolean(displayName !== 'Unknown' || summary.phone || direction);

  if (!callTraceId && !cxoneContactId) {
    return null;
  }

  return (
    <section className={styles.card} aria-label={t('acw:callDetails.title')}>
      <div className={styles.header}>
        <div className={styles.headerMain}>
          <h3 className={styles.title}>{t('acw:callDetails.title')}</h3>
          {(direction || (!isLoading && hasSummaryContent)) && (
            <div className={styles.summary}>
              {direction && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>{t('acw:callDetails.callType')}</span>
                  <CallDirectionBadge direction={direction} />
                </div>
              )}
              {!isLoading && displayName !== 'Unknown' && (
                <span className={styles.summaryName}>{displayName}</span>
              )}
              {!isLoading && summary.phone && (
                <span className={styles.summaryMeta}>{formatPhoneForDisplay(summary.phone)}</span>
              )}
              {!isLoading && summary.startedAt && (
                <span className={styles.summaryTime}>
                  {formatTraceTimestamp(summary.startedAt)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={styles.body}>
        {isLoading ? (
          <div className={activityLogStyles.traceDetailsLoading}>
            {t('activityLog:loadingEvents')}
          </div>
        ) : error ? (
          <div className={activityLogStyles.traceDetailsError}>{error}</div>
        ) : events.length === 0 ? (
          <div className={activityLogStyles.traceDetailsEmpty}>{t('activityLog:noEvents')}</div>
        ) : (
          <CallActivityEventTimeline events={events} />
        )}
      </div>
    </section>
  );
}

export default AcwCallTraceTimeline;
